import React, { useState, useEffect, useMemo } from "react";
import "./PrivateCourses.css";
import { Link, useNavigate } from "react-router-dom";
import { BiCategory } from "react-icons/bi";
import { IoIosSearch } from "react-icons/io";
import { FaGraduationCap, FaClock, FaDollarSign, FaBookOpen, FaAward, FaChevronDown, FaChevronUp, FaCheck, FaMapMarkerAlt } from "react-icons/fa";
import api from "../../utils/api";
import { detectUserCountry } from "../../utils/detectCountry";

const PrivateCourses = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [countryDetected, setCountryDetected] = useState(false);
  const [usdToNgn, setUsdToNgn] = useState(null);
  const [rateLoading, setRateLoading] = useState(true);
  const [expandedCurriculum, setExpandedCurriculum] = useState({});

  useEffect(() => {
    // Fetch all courses
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const response = await api.get("/courses"); 
        if (response.data && response.data.data) {
          setCourses(response.data.data);
        } else {
          setCourses(response.data || []);
        }
      } catch (err) { 
        setError("Unable to load private courses. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();

    // Auto-detect visitor's country and set currency accordingly
    const autoDetectCurrency = async () => {
      const { currency: detectedCurrency } = await detectUserCountry();
      setCurrency(detectedCurrency);
      setCountryDetected(true);
    };
    autoDetectCurrency();

    // Fetch exchange rate
    const fetchRate = async () => {
      setRateLoading(true);
      try {
        const res = await fetch("https://v6.exchangerate-api.com/v6/689d396c0a109d335cc4092c/latest/USD");
        const data = await res.json();
        setUsdToNgn(data.conversion_rates?.NGN);
      } catch (e) {
        setUsdToNgn(null);
      } finally {
        setRateLoading(false);
      }
    };
    fetchRate();
  }, []);

  // Filter courses where course type is private
  const privateCourses = useMemo(() => {
    if (!courses) return [];
    
    return courses.filter((course) => {
      // 1. Must have type 'private'
      const isPrivateType = course.type === "private";
      if (!isPrivateType) return false;

      // 2. Hide courses that are inactive if applicable
      if (course.status?.toLowerCase() === "inactive") return false;

      // 3. Search filter
      const matchesSearch =
        searchTerm === "" ||
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (course.category && course.category.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesSearch;
    });
  }, [courses, searchTerm]);

  const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  const handleRegisterCourse = (course) => {
    setTimeout(() => {
      navigate("/cohort/register", {
        state: {
          cohort_id: course.cohorts?.[0]?.id || null,
          course_id: course.id,
          cohortName: course.title,
          coursePrice: course.price, // Private Class Price
          cohortPrice: course.cohorts?.[0]?.dollar_price || null,
          isPrivateClass: true, // Custom flag to set Private Class option
        },
      });
    }, 250);
  };

  const toggleCurriculum = (courseId) => {
    setExpandedCurriculum((prev) => ({
      ...prev,
      [courseId]: !prev[courseId],
    }));
  };

  return (
    <div className="private-courses-page">
      {/* Hero Section */}
      <section className="private-courses-hero">
        <div className="private-courses-hero-grid" aria-hidden="true" />
        <div className="private-courses-hero-content">
          <div className="private-courses-hero-badge">
            <span className="badge-dot" />
            1-on-1 Personalized Training
          </div>
          <h1 className="private-courses-hero-title">
                <span>Private</span> Classes
          </h1>
          <p className="private-courses-hero-subtitle">
            Skip the cohort schedules and learn at your own pace. Gain job-ready tech mastery
            with customized curriculum schedules, direct mentor reviews, and dedicated private access.
          </p>
        </div>
      </section>

      {/* Main content grid */}
      <main className="private-courses-main">
        {/* Search & Currency Toggles */}
        <div className="private-courses-search-bar mb-5">
          <div className="private-search-wrapper">
            <IoIosSearch className="private-search-icon" />
            <input
              type="text"
              placeholder="Search private courses by title or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="private-search-input"
            />
          </div>

          <div className="private-currency-toggle">
            <span className="currency-label">
              Currency:
              {!countryDetected && <span className="currency-detecting">Detecting…</span>}
            </span>
            <div className="currency-btn-group">
              <button
                className={`currency-toggle-btn ${currency === "NGN" ? "active" : ""}`}
                onClick={() => setCurrency("NGN")}
              >
                ₦ NGN
              </button>
              <button
                className={`currency-toggle-btn ${currency === "USD" ? "active" : ""}`}
                onClick={() => setCurrency("USD")}
              >
                $ USD
              </button>
            </div>
          </div>
        </div>

        {/* Courses Listing Grid */}
        {loading ? (
          <div className="private-courses-grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className="private-skeleton-card">
                <div className="skeleton-image" />
                <div className="skeleton-body">
                  <div className="skeleton-line skeleton-title" />
                  <div className="skeleton-line skeleton-badge" />
                  <div className="skeleton-line skeleton-text" />
                  <div className="skeleton-line skeleton-text short" />
                  <div className="skeleton-actions" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="private-courses-error">
            <div className="error-icon">⚠️</div>
            <h3>Something went wrong</h3>
            <p>{error}</p>
            <button className="retry-btn" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        ) : privateCourses.length === 0 ? (
          <div className="private-courses-empty">
            <div className="empty-icon">
              <FaGraduationCap />
            </div>
            <h3>No Private Courses Available</h3>
            <p>
              We are currently designing fully personalized private programs. Check back soon or contact support for customized modules!
            </p>
          </div>
        ) : (
          <div className="private-courses-grid">
            {privateCourses.map((course) => {
              const hasCurriculum = course.curriculum && course.curriculum.length > 0;
              const hasPrerequisites = course.prerequisite && course.prerequisite.length > 0;
              const duration = course.cohorts?.[0]?.duration || "Flexible / Self-Paced";
              const detailsSlug = course.cohorts?.[0]?.slug || course.slug;
              
              return (
                <article key={course.id} className="private-course-premium-card">
                  <div className="card-image-wrapper">
                    <div
                      className="card-background-image"
                      style={{
                        backgroundImage: course.image
                          ? `url(${course.image})`
                          : "linear-gradient(135deg, #0b7fff 0%, #1a237e 100%)",
                      }}
                    />
                    <div className="card-image-overlay">
                      <span className="category-badge">
                        <BiCategory className="badge-icon" />
                        {course.category || "Tech"}
                      </span>
                      {/* <span className={`status-badge active`}>
                        Private Registration Open
                      </span> */}
                    </div>
                  </div>

                  <div className="card-content-inner">
                    <h3 className="course-title">{course.title}</h3>
                    
                    <p className="course-description">
                      {stripHtml(course.description).substring(0, 240)}...
                    </p>

                    {/* Metadata features */}
                    <div className="course-feature-grid mb-4">
                      {course.location && (
                        <div className="feature-item">
                          <FaMapMarkerAlt className="feature-icon" color="#2563eb" />
                          <div>
                            <span className="feature-label">Location</span>
                            <span className="feature-value">{course.location}</span>
                          </div>
                        </div>
                      )}
                      {/* <div className="feature-item">
                        <FaClock className="feature-icon" />
                        <div>
                          <span className="feature-label">Duration</span>
                          <span className="feature-value">{duration}</span>
                        </div>
                      </div>
                      <div className="feature-item">
                        <FaGraduationCap className="feature-icon" />
                        <div>
                          <span className="feature-label">Format</span>
                          <span className="feature-value">Private 1-on-1</span>
                        </div>
                      </div> */}
                      <div className="feature-item feature-price-item">
                        <FaDollarSign className="feature-icon" />
                        <div>
                          <span className="feature-label">Price</span>
                          <span className="feature-value price-tag">
                            {currency === "USD" ? (
                              <>
                                <span>$</span>
                                {course.price
                                  ? parseFloat(course.price).toLocaleString()
                                  : "N/A"}
                              </>
                            ) : (
                              <>
                                <span>₦</span>
                                {rateLoading ? (
                                  <span>Loading...</span>
                                ) : usdToNgn && course.price ? (
                                  (parseFloat(course.price) * usdToNgn).toLocaleString()
                                ) : (
                                  "N/A"
                                )}
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Expandable Curriculum */}
                    {hasCurriculum && (
                      <div className="curriculum-expand-section mb-3">
                        <button
                          onClick={() => toggleCurriculum(course.id)}
                          className="curriculum-toggle-btn-expand"
                        >
                          <FaBookOpen className="me-2" />
                          <span>Course Syllabus / Modules</span>
                          {expandedCurriculum[course.id] ? <FaChevronUp className="ms-auto" /> : <FaChevronDown className="ms-auto" />}
                        </button>

                        {expandedCurriculum[course.id] && (
                          <ul className="curriculum-syllabus-list animate-slide-down">
                            {course.curriculum.map((module, mIdx) => (
                              <li key={mIdx} className="syllabus-module-item">
                                <div className="module-header">
                                  <span className="module-dot" />
                                  <strong>{module.title}</strong>
                                </div>
                                {module.description && <p className="module-desc">{module.description}</p>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                    {/* Prerequisites */}
                    {hasPrerequisites && (
                      <div className="prerequisites-section mb-4">
                        <h5 className="section-title"><FaAward className="me-2" />Prerequisites:</h5>
                        <ul className="prereq-list">
                          {course.prerequisite.map((prereq, pIdx) => (
                            <li key={pIdx}>
                              <FaCheck className="check-icon" />
                              <span>{prereq.title}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="course-actions-group">
                      <button
                        onClick={() => handleRegisterCourse(course)}
                        className="enroll-now-btn"
                      >
                        Enroll in Private Class
                      </button>
                      <Link to={`/private-courses/${course.slug}`} className="view-details-outline">
                        View Full Details
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default PrivateCourses;
